import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { FormattedMessage } from "react-intl";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import sh from "bundles/common/utils/sh";
import CompanyCard from "bundles/Candidate/components/CompanyCard";

const SCROLL_AMOUNT = 340;
const AUTO_SCROLL_SPEED = 60; // pixels per second
const PAUSE_AFTER_INTERACTION = 3000; // when using arrows, pause auto-scroll

const CompaniesSection = () => {
  const locale = useSelector((state) => state.i18nLocale);
  const theme = useTheme();
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const containerRef = useRef(null);
  const rafRef = useRef(null);
  const pausedRef = useRef(false);
  const resumeTimerRef = useRef(null);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    sh.get("featured_companies")
      .then((res) => setCompanies(res.data.companies || []))
      .catch((err) => console.error(err));
  }, []);

  // Creates the illusion of infinite scrolling by duplicating the company list
  useEffect(() => {
    if (companies.length === 0) return;

    let lastTime = performance.now();

    const animate = (time) => {
      const delta = time - lastTime;
      lastTime = time;
      const el = containerRef.current;

      if (el && !pausedRef.current) {
        el.scrollLeft += AUTO_SCROLL_SPEED * (delta / 1000);

        // When past the first set, jump back
        const halfScroll = el.scrollWidth / 2;
        if (halfScroll > 0 && el.scrollLeft >= halfScroll) {
          el.scrollLeft -= halfScroll;
        }
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(resumeTimerRef.current);
    };
  }, [companies]);

  const pauseTemporarily = useCallback(() => {
    pausedRef.current = true;
    clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      pausedRef.current = false;
    }, PAUSE_AFTER_INTERACTION);
  }, []);

  const pause = useCallback(() => {
    pausedRef.current = true;
    clearTimeout(resumeTimerRef.current);
  }, []);

  const resume = useCallback(() => {
    pausedRef.current = false;
  }, []);

  const scroll = useCallback((direction) => {
    const el = containerRef.current;
    if (!el) return;
    pauseTemporarily();
    el.scrollBy({
      left: direction === "left" ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: "smooth",
    });
  }, [pauseTemporarily]);

  if (companies.length === 0) return null;

  const duplicated = [...companies, ...companies, ...companies];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: "3rem", md: "4rem" },
        overflow: "hidden",
        bgcolor: "hsl(160 10% 96% / 0.4)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          maxWidth: "72rem",
          mx: "auto",
          px: { xs: "1rem", sm: "1.5rem", lg: "2rem" },
          mb: "2.5rem",
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "flex-end" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: "1rem",
          }}
        >
          <Box>
            <Typography
              sx={{
                display: "block",
                mb: "0.5rem",
                fontSize: "0.875rem",
                fontWeight: 700,
                color: "hsl(153 68% 25%)",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              }}
            >
              <FormattedMessage id="companies_section.eyebrow" />
            </Typography>
            <Typography
              variant="h2"
              sx={{
                m: 0,
                fontSize: { xs: "1.875rem", md: "2.25rem" },
                fontWeight: 900,
                color: "hsl(178 93% 10%)",
                letterSpacing: "-0.02em",
                fontFamily: "'Segoe UI', 'Work Sans', sans-serif",
              }}
            >
              <FormattedMessage id="companies_section.heading" />
            </Typography>
            <Typography
              sx={{
                mt: "0.5rem",
                fontSize: { xs: "1.1rem", md: "1rem" },
                color: "hsl(178 12% 46%)",
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              }}
            >
              <FormattedMessage id="companies_section.subheading" />
            </Typography>
          </Box>

          {/* Scroll arrows */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: "0.5rem" }}>
            {["left", "right"].map((dir) => (
              <Box
                key={dir}
                component="button"
                onClick={() => scroll(dir)}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  border: 1,
                  borderColor: "hsla(160, 10%, 90%, 0.6)",
                  bgcolor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    bgcolor: "hsla(153, 68%, 25%, 0.08)",
                    borderColor: "hsla(153, 68%, 25%, 0.3)",
                  },
                }}
              >
                {dir === "left" ? (
                  <ChevronLeft size={18} style={{ color: "hsl(178 93% 10%)" }} />
                ) : (
                  <ChevronRight size={18} style={{ color: "hsl(178 93% 10%)" }} />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Scrollable cards */}
      <Box
        ref={containerRef}
        onMouseEnter={pause}
        onMouseLeave={resume}
        onTouchStart={pauseTemporarily}
        sx={{
          display: "flex",
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          WebkitOverflowScrolling: "touch",
          px: { xs: "1rem", lg: "calc((100vw - 72rem) / 2 + 2rem)" },
        }}
      >
        {duplicated.map((company, i) => (
          <Box
            key={`${company.id}-${i}`}
            sx={{
              minWidth: 320,
              maxWidth: 320,
              flexShrink: 0,
              mr: "1.25rem",
            }}
          >
            <CompanyCard
              company={company}
              onClick={() => {
                window.location.href = `/${i18nLocale}/companies/${company.encoded_name}/${company.id}`;
              }}
              index={i % companies.length} />
          </Box>
        ))}
      </Box>
      <Box sx={{ textAlign: "center" }}>
        <Box
          component="a"
          href={`/${locale}/companies`}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            px: "1.5rem",
            py: "0.875rem",
            mt: "2.5rem",
            bgcolor: "hsl(153 68% 25%)",
            color: theme.palette.common.white,
            fontWeight: 700,
            fontSize: "0.875rem",
            borderRadius: "0.75rem",
            textDecoration: "none",
            transition: "background-color 0.2s ease",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            "&:hover": {
              bgcolor: "hsl(153 68% 25% / 0.9)",
            },
            "& .arrow-icon": {
              transition: "transform 0.2s ease",
            },
            "&:hover .arrow-icon": {
              transform: "translateX(2px)",
            },
          }}
        >
          <FormattedMessage id="companies_section.call_to_action" />
          <ArrowRight size={14} className="arrow-icon" />
        </Box>
      </Box>
    </Box>
  );
};

export default CompaniesSection;